import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/modules/admin';

interface MarshmallowCardProps {
  userId: string | undefined;
}

export const MarshmallowCard: React.FC<MarshmallowCardProps> = ({ userId }) => {
  const [remaining, setRemaining] = useState<number | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [resetDate, setResetDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [amount, setAmount] = useState('');
  const [adding, setAdding] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchBalance = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      setError(null);
      const result = await adminApi.getUserMarshmallows(userId);
      setRemaining(result.data.remaining);
      setTotal(result.data.total);
      setResetDate(result.data.resetDate);
    } catch (err) {
      setError('Failed to load marshmallow balance');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleAdd = async () => {
    if (!userId) return;
    const parsed = parseInt(amount, 10);
    if (!parsed || parsed <= 0) return;

    try {
      setAdding(true);
      setError(null);
      setSuccessMessage(null);
      const result = await adminApi.addUserMarshmallows(userId, parsed);
      setRemaining(result.data.newRemaining);
      setTotal(result.data.newTotal);
      setAmount('');
      setSuccessMessage(result.message);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError('Failed to add marshmallows');
    } finally {
      setAdding(false);
    }
  };

  const percentage =
    remaining !== null && total !== null && total > 0 ? Math.round((remaining / total) * 100) : 0;

  const isLow = remaining !== null && total !== null && remaining < total * 0.1;
  const isDepleted = remaining === 0;

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Marshmallows</h3>
        <p className="text-sm text-gray-500 mt-1">Roary AI credit balance for this user</p>
      </div>
      <div className="px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Balance display */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-gray-900">
                  {remaining ?? 0}
                  <span className="text-lg font-normal text-gray-500"> / {total ?? 0}</span>
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {isDepleted ? (
                    <span className="text-red-600 font-medium">Depleted</span>
                  ) : isLow ? (
                    <span className="text-amber-600 font-medium">Running low</span>
                  ) : (
                    'marshmallows remaining'
                  )}
                </p>
              </div>
              {resetDate && (
                <div className="text-right">
                  <p className="text-xs text-gray-500">Resets</p>
                  <p className="text-sm text-gray-700">
                    {new Date(resetDate).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>

            {/* Progress bar */}
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className={`h-2.5 rounded-full transition-all ${
                  isDepleted ? 'bg-red-500' : isLow ? 'bg-amber-500' : 'bg-green-500'
                }`}
                style={{ width: `${percentage}%` }}
              />
            </div>

            {/* Messages */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
            {successMessage && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-600">{successMessage}</p>
              </div>
            )}

            {/* Add marshmallows form */}
            <div className="pt-4 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Add Marshmallows
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="number"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder="Amount"
                  min="1"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <button
                  onClick={handleAdd}
                  disabled={adding || !amount || parseInt(amount, 10) <= 0}
                  className="btn btn-primary whitespace-nowrap"
                >
                  {adding ? 'Adding...' : 'Add'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Adds on top of the current balance. Does not affect the reset cycle.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
